/**
 * FormStep component for rendering form fields within a step.
 * Uses extracted field components for better maintainability.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import type { FormFieldConfig, FormValues } from "./types";
import { VALIDATION_KEYS } from "./types";
import {
  TextField,
  SelectField,
  SwitchField,
  CheckboxField,
  TextareaField,
  FileField,
} from "./fields";
import type { FieldProps } from "./fields";

interface FormStepProps {
  fields: FormFieldConfig[];
  values: FormValues;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  onChange: (name: string, value: unknown) => void;
  onBlur: (name: string) => void;
}

/**
 * Map of field types to their respective components.
 */
const FIELD_COMPONENTS: Record<
  string,
  React.ComponentType<FieldProps>
> = {
  text: TextField,
  email: TextField,
  password: TextField,
  tel: TextField,
  url: TextField,
  number: TextField,
  select: SelectField,
  switch: SwitchField,
  checkbox: CheckboxField,
  textarea: TextareaField,
  file: FileField,
};

export function FormStep({
  fields,
  values,
  errors,
  touched,
  onChange,
  onBlur,
}: FormStepProps) {
  const { t } = useTranslation();

  /**
   * Translate validation error messages.
   * If the error is an i18n key (starts with "onboarding."), translate it.
   * Otherwise return the original message.
   */
  const translateError = (error: string | undefined, field: FormFieldConfig): string | undefined => {
    if (!error) return undefined;

    // Check if error is an i18n key (starts with "onboarding.")
    if (error.startsWith("onboarding.")) {
      // Get field-specific values for interpolation
      const interpolation: Record<string, unknown> = {};

      if (error === VALIDATION_KEYS.minLength && field.validation?.minLength) {
        interpolation.min = field.validation.minLength;
      }
      if (error === VALIDATION_KEYS.maxLength && field.validation?.maxLength) {
        interpolation.max = field.validation.maxLength;
      }
      if (error === VALIDATION_KEYS.fileTooLarge && field.maxFileSize) {
        interpolation.max = Math.round(field.maxFileSize / 1048576);
      }

      return t(error, interpolation);
    }

    // Return original message if not an i18n key
    return error;
  };

  /**
   * Get translated field text.
   */
  const getFieldText = (
    fieldName: string,
    key: "label" | "placeholder" | "description" | "extendedDescription",
    fallback?: string
  ): string => {
    const translationKey = `onboarding.fields.${fieldName}.${key}`;
    const translated = t(translationKey);
    return translated !== translationKey ? translated : fallback || "";
  };

  /**
   * Get translated option label for select fields.
   */
  const getOptionLabel = (
    fieldName: string,
    optionValue: string,
    fallback: string
  ): string => {
    const translationKey = `onboarding.fields.${fieldName}.options.${optionValue}`;
    const translated = t(translationKey);
    return translated !== translationKey ? translated : fallback;
  };

  /**
   * Check if a field should be visible based on dependencies.
   */
  const shouldShowField = (field: FormFieldConfig): boolean => {
    if (!field.dependsOn) return true;

    const dependentValue = values[field.dependsOn.field];
    const { condition, value } = field.dependsOn;

    switch (condition) {
      case "equals":
        return dependentValue === value;
      case "notEquals":
        return dependentValue !== value;
      case "truthy":
        return Boolean(dependentValue);
      case "falsy":
        return !dependentValue;
      default:
        return true;
    }
  };

  /**
   * Render a single field using the appropriate component.
   */
  const renderField = (field: FormFieldConfig) => {
    if (!shouldShowField(field)) return null;

    const FieldComponent = FIELD_COMPONENTS[field.type] || TextField;
    const hasError = touched[field.name] && errors[field.name];
    // Translate error message if it's an i18n key
    const translatedError = hasError ? translateError(errors[field.name], field) : undefined;

    const fieldProps: FieldProps = {
      field,
      value: values[field.name],
      touched: touched[field.name] || false,
      error: translatedError,
      label: getFieldText(field.name, "label", field.label),
      placeholder: getFieldText(field.name, "placeholder", field.placeholder),
      description: getFieldText(field.name, "description", field.description),
      extendedDescription: getFieldText(field.name, "extendedDescription", field.extendedDescription),
      onChange,
      onBlur,
      getOptionLabel: (optionValue: string, fallback: string) =>
        getOptionLabel(field.name, optionValue, fallback),
    };

    return <FieldComponent key={field.name} {...fieldProps} />;
  };

  return <div className="space-y-6">{fields.map(renderField)}</div>;
}
